import { Play, Pause, RotateCcw, RotateCw, Volume2, Share2 } from "lucide-react";
import { useArticleAudioPlayer } from "@/context/ArticleAudioPlayerContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useT } from "@/i18n/useLocale";

interface FullArticlePlayerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FullArticlePlayer = ({ open, onOpenChange }: FullArticlePlayerProps) => {
  const t = useT();
  const {
    currentArticle,
    isPlaying,
    currentTime,
    duration,
    volume,
    playbackRate,
    pause,
    resume,
    seek,
    setVolume,
    setPlaybackRate,
  } = useArticleAudioPlayer();

  if (!currentArticle) return null;

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleSeek = (value: number[]) => {
    seek(value[0]);
  };

  const handleSkipBackward = () => {
    seek(Math.max(0, currentTime - 15));
  };

  const handleSkipForward = () => {
    seek(Math.min(duration, currentTime + 15));
  };

  const togglePlaybackRate = () => {
    const rates = [1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % rates.length;
    setPlaybackRate(rates[nextIndex]);
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: currentArticle.title,
          text: `Listen to: ${currentArticle.title}`,
        });
      } else {
        toast.success("Link copied to clipboard");
      }
    } catch (error) {
      console.error("Share failed:", error);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="h-[90vh] p-0 border-t-2 border-border bg-background"
      >
        <div className="h-full flex flex-col bg-background">
          {/* Drag handle */}
          <div className="flex justify-center pt-4 pb-2">
            <div className="w-12 h-1 rounded-full bg-border" />
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            {/* Artwork */}
            <div className="w-full aspect-square max-w-sm mx-auto mb-8 rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-byteline-green/20 to-blue-500/20">
              {currentArticle.imageUrl ? (
                <img
                  src={currentArticle.imageUrl}
                  alt={currentArticle.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-6xl">📰</div>
                </div>
              )}
            </div>

            {/* Article info */}
            <div className="space-y-2 mb-8">
              <h2 className="text-2xl font-bold leading-tight">
                {currentArticle.title}
              </h2>
              <p className="text-muted-foreground">{currentArticle.source}</p>
            </div>

            {/* Progress slider */}
            <div className="space-y-2 mb-8">
              <Slider
                value={[currentTime]}
                max={duration || 100}
                step={1}
                onValueChange={handleSeek}
                className="cursor-pointer"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Main controls */}
            <div className="flex items-center justify-center gap-6 mb-8">
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("auto.skip-backward-15-seconds-4da1")}
                className="h-12 w-12"
                onClick={handleSkipBackward}
              >
                <RotateCcw className="h-6 w-6" />
              </Button>

              <Button
                size="icon"
                aria-label={isPlaying ? "Pause article" : "Play article"}
                className="h-16 w-16 rounded-full bg-byteline-green hover:bg-byteline-green/90"
                onClick={() => (isPlaying ? pause() : resume())}
              >
                {isPlaying ? (
                  <Pause className="h-8 w-8 fill-white" />
                ) : (
                  <Play className="h-8 w-8 fill-white ml-1" />
                )}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                aria-label={t("auto.skip-forward-15-seconds-087f")}
                className="h-12 w-12"
                onClick={handleSkipForward}
              >
                <RotateCw className="h-6 w-6" />
              </Button>
            </div>

            {/* Secondary controls */}
            <div className="flex items-center justify-between px-4 mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={togglePlaybackRate}
                className="font-mono"
              >
                {playbackRate}x
              </Button>

              <Button
                variant="ghost"
                size="icon"
                aria-label={t("auto.share-5a95")}
                onClick={handleShare}
              >
                <Share2 className="h-5 w-5" />
              </Button>
            </div>

            {/* Volume control */}
            <div className="flex items-center gap-3 px-4">
              <Volume2 className="h-5 w-5 text-muted-foreground" />
              <Slider
                value={[volume * 100]}
                max={100}
                step={1}
                onValueChange={(value) => setVolume(value[0] / 100)}
                className="flex-1"
              />
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FullArticlePlayer;
